import type { LogTransport } from '../transport/logTransport';
import { chainFromAia } from './aia';
import { chainFromCrtsh } from './crtsh';
import { chainFromWhatsMyChainCert } from './wmcc';
import { SOURCE_LABEL, type ChainSource, type DiscoveredChain } from './types';
import { completeWithRoots } from './systemRoots';

export interface DiscoverOptions {
  transport: LogTransport;
  /** Sources to try, in order. */
  sources: ChainSource[];
  /** Pure-web route: find the chain in a CT log entry (needs the app's registry/clients). */
  fromLogs?: (chain: Uint8Array[]) => Promise<DiscoveredChain | undefined>;
  /** Desktop: the OS trust store, to append the root a chain hangs from. */
  systemRoots?: () => Promise<Uint8Array[]>;
  signal?: AbortSignal;
}

export class ChainDiscoveryError extends Error {
  override name = 'ChainDiscoveryError';
  constructor(readonly attempts: Array<{ source: ChainSource; error: string }>) {
    super(attempts.map((a) => `${SOURCE_LABEL[a.source]}: ${a.error}`).join('\n'));
  }
}

/** Tries each source in turn until one returns a chain that grows the input. */
export async function discoverChain(chain: Uint8Array[], opts: DiscoverOptions): Promise<DiscoveredChain> {
  const attempts: Array<{ source: ChainSource; error: string }> = [];
  for (const source of opts.sources) {
    try {
      let r: DiscoveredChain | undefined;
      if (source === 'whatsmychaincert') r = await chainFromWhatsMyChainCert(chain[0]!, opts.transport, opts.signal);
      else if (source === 'ct-log') r = await opts.fromLogs?.(chain);
      else if (source === 'crt.sh') r = await chainFromCrtsh(chain[0]!, opts.transport, opts.signal);
      else if (source === 'aia') r = await chainFromAia(chain, opts.transport, opts.signal ? { signal: opts.signal } : {});
      else if (source === 'system-roots') r = opts.systemRoots ? completeWithRoots(chain, await opts.systemRoots()) : undefined;
      if (r && r.chain.length > chain.length) return r;
      attempts.push({ source, error: r ? 'found nothing beyond what was given' : 'not available' });
    } catch (e) {
      attempts.push({ source, error: e instanceof Error ? e.message : String(e) });
    }
  }
  throw new ChainDiscoveryError(attempts);
}
